/**
 * Derives the brand tokens of the administration (see THEME.md of the theme module) from one main color, for the light
 * and the dark theme. Lighter or darker variants are searched until they reach a readable contrast, so even very light
 * or very dark main colors keep headings, links and filled buttons legible.
 */
export type Rgb = [number, number, number];

const WHITE: Rgb = [255, 255, 255];

/** Surface of the dark theme, the contrast reference for dark values */
const DARK_SURFACE: Rgb = [27, 41, 47];

/** Parses #rgb and #rrggbb, returns null for anything else */
export function parseHex(color: string): Rgb | null {
  const match = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec((color || '').trim());
  if (!match) {
    return null;
  }
  const hex = match[1].length === 3 ? match[1].split('').map(c => c + c).join('') : match[1];
  return [0, 2, 4].map(i => parseInt(hex.substr(i, 2), 16)) as Rgb;
}

export function toHex(rgb: Rgb): string {
  return '#' + rgb.map(c => Math.round(Math.max(0, Math.min(255, c))).toString(16).padStart(2, '0')).join('');
}

/** Mixes color a with color b, amount 0 keeps a, 1 gives b */
export function mix(a: Rgb, b: Rgb, amount: number): Rgb {
  return a.map((c, i) => c + (b[i] - c) * amount) as Rgb;
}

/** Relative luminance (WCAG) */
export function luminance(rgb: Rgb): number {
  const [r, g, b] = rgb.map(c => {
    const v = c / 255;
    return v <= .03928 ? v / 12.92 : Math.pow((v + .055) / 1.055, 2.4);
  });
  return .2126 * r + .7152 * g + .0722 * b;
}

/** Contrast ratio (WCAG) */
export function contrast(a: Rgb, b: Rgb): number {
  const [light, dark] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (light + .05) / (dark + .05);
}

/** Moves the color towards target in small steps until it reaches the contrast against the reference */
function untilContrast(color: Rgb, target: Rgb, reference: Rgb, ratio: number): Rgb {
  for (let step = 0; step <= 20; step++) {
    const candidate = mix(color, target, step / 20);
    if (contrast(candidate, reference) >= ratio) {
      return candidate;
    }
  }
  return target;
}

/** Light and dark brand tokens for the given main color, null for an invalid color */
export function adminPalette(mainColor: string): {light: {[token: string]: string}, dark: {[token: string]: string}} | null {
  const main = parseHex(mainColor);
  if (!main) {
    return null;
  }
  const black: Rgb = [0, 0, 0];

  // light: dark brand color for text and filled buttons, the main color itself for highlights
  const primary = untilContrast(main, black, WHITE, 7);
  const accent = untilContrast(main, black, WHITE, 2.6);
  const light = {
    '--mm-primary': toHex(primary),
    '--mm-primary-strong': toHex(mix(primary, black, .15)),
    '--mm-accent': toHex(accent),
    '--mm-accent-strong': toHex(mix(accent, black, .2)),
    '--mm-accent-rgb': accent.map(Math.round).join(', '),
    '--mm-soft': toHex(mix(accent, WHITE, .88)),
    '--mm-soft-strong': toHex(mix(accent, WHITE, .82)),
    '--mm-accent-border': toHex(mix(accent, WHITE, .7)),
    '--mm-control-accent': toHex(untilContrast(main, black, WHITE, 4.5)),
    '--mm-link': toHex(untilContrast(main, black, WHITE, 6)),
    '--mm-fill-hover': toHex(mix(primary, WHITE, .14)),
    '--mm-fill-accent-hover': toHex(mix(accent, black, .12)),
    '--mm-on-fill': '#ffffff',
    '--mm-shadow-rgb': mix(primary, black, .3).map(Math.round).join(', ')
  };
  // tokens that only reference other tokens are resolved where they are declared, so they are declared again
  Object.assign(light, {
    '--mm-fill': light['--mm-primary'],
    '--mm-fill-border': light['--mm-primary-strong'],
    '--mm-fill-accent': light['--mm-accent'],
    '--mm-success': light['--mm-accent'],
    '--mm-badge-primary': light['--mm-primary'],
    '--mm-badge-success': light['--mm-accent'],
    '--mm-alert-info-bg': light['--mm-soft-strong'],
    '--mm-alert-info-text': light['--mm-primary'],
    '--mm-alert-info-border': light['--mm-accent-border'],
    '--mm-focus-ring': `0 0 0 3px rgba(${light['--mm-accent-rgb']}, .102)`
  });

  // dark: light brand colors on the dark surfaces
  const darkPrimary = untilContrast(main, WHITE, DARK_SURFACE, 8);
  const darkAccent = untilContrast(main, WHITE, DARK_SURFACE, 4.5);
  const darkFillHover = mix(darkAccent, WHITE, .2);
  const dark = {
    '--mm-primary': toHex(darkPrimary),
    '--mm-accent': toHex(darkAccent),
    '--mm-accent-rgb': darkAccent.map(Math.round).join(', '),
    '--mm-soft': toHex(mix(darkAccent, DARK_SURFACE, .78)),
    '--mm-soft-strong': toHex(mix(darkAccent, DARK_SURFACE, .7)),
    '--mm-accent-border': toHex(mix(darkAccent, DARK_SURFACE, .62)),
    '--mm-fill-hover': toHex(darkFillHover),
    '--mm-fill-accent-hover': toHex(darkFillHover),
    '--mm-on-fill': contrast(darkAccent, WHITE) >= 4.5 ? '#ffffff' : '#102824'
  };
  Object.assign(dark, {
    '--mm-fill': dark['--mm-accent'],
    '--mm-fill-border': dark['--mm-accent'],
    '--mm-fill-accent': dark['--mm-accent'],
    '--mm-control-accent': dark['--mm-accent'],
    '--mm-link': dark['--mm-primary'],
    '--mm-success': dark['--mm-accent'],
    '--mm-badge-primary': dark['--mm-accent'],
    '--mm-badge-success': dark['--mm-accent'],
    '--mm-focus-ring': `0 0 0 3px rgba(${dark['--mm-accent-rgb']}, .2)`
  });

  return {light, dark};
}
